import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Check, AlertTriangle, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_REJECTION_TEMPLATE = (
  "Thank you so much for putting your creativity into this draft. " +
  "After careful consideration, we've decided to pass on this round — " +
  "but your craft is real, and we'd love to keep you on the radar for our next wave."
);

/**
 * DraftDecisionPanel — the Approve / Request changes / Pass UI for a single draft.
 *
 * Pass (== reject in the SSP API) uses a softer label and a kind default
 * feedback template that pre-fills when the user picks it. Brand-decision
 * is sent as the literal SSP enum (`approve | revise | reject`).
 */
export default function DraftDecisionPanel({ draft, onResolved }) {
  const [decision, setDecision] = useState(null); // 'approve' | 'revise' | 'reject'
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const choose = (which) => {
    setDecision(which);
    setError(null);
    if (which === 'reject' && !feedback.trim()) {
      setFeedback(KIND_REJECTION_TEMPLATE);
    }
  };

  const submit = async () => {
    if (!decision) return;
    if ((decision === 'revise' || decision === 'reject') && !feedback.trim()) {
      setError('Please share a quick note for the creator.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/brand/drafts/${draft.id}/decision`,
        { decision, feedback: feedback.trim(), revision_max_rounds: 2 },
        { withCredentials: true }
      );
      if (!data.ok) {
        setError(data.ssp_reason || 'Could not submit decision');
        return;
      }
      onResolved && onResolved({ decision, local_status: data.local_status });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  const baseBtn = "flex-1 py-3 rounded-lg text-xs font-bold transition border-2 flex items-center justify-center gap-1.5";
  const inactive = "border-[#1f1f1f] bg-[#0A0A0A] text-gray-400 hover:border-[#00D4FF]";
  const sel = (key, color) => decision === key ? `border-${color}-400 bg-${color}-500/15 text-${color}-300` : inactive;

  return (
    <div className="rounded-2xl border border-[#1f1f1f] bg-[#0A0A0A] p-5 space-y-4" data-testid="draft-decision-panel">
      <div className="flex gap-2">
        <button onClick={() => choose('approve')} disabled={busy} data-testid="decision-approve-btn"
                className={`${baseBtn} ${decision === 'approve' ? 'border-emerald-400 bg-emerald-500/15 text-emerald-300' : inactive}`}>
          <Check className="h-3.5 w-3.5" /> Approve
        </button>
        <button onClick={() => choose('revise')} disabled={busy} data-testid="decision-revise-btn"
                className={`${baseBtn} ${decision === 'revise' ? 'border-amber-400 bg-amber-500/15 text-amber-300' : inactive}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> Request changes
        </button>
        <button onClick={() => choose('reject')} disabled={busy} data-testid="decision-reject-btn"
                className={`${baseBtn} ${decision === 'reject' ? 'border-rose-400 bg-rose-500/15 text-rose-300' : inactive}`}>
          <X className="h-3.5 w-3.5" /> Pass on this round
        </button>
      </div>

      {decision && decision !== 'approve' && (
        <div>
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
            {decision === 'revise' ? 'What should change?' : 'A note for the creator'}
            <span className="text-rose-400">*</span>
          </label>
          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            data-testid="decision-feedback-input"
            rows={4}
            className="w-full mt-1.5 px-3 py-2.5 rounded-lg bg-[#121212] border border-[#1f1f1f] focus:border-[#00D4FF] text-sm outline-none transition"
            placeholder={decision === 'revise' ? 'Be specific — angle, hook, captioning…' : 'Kind, honest, encouraging.'}
          />
          {decision === 'reject' && (
            <p className="mt-1.5 text-[10px] text-gray-500">
              Escrow will be refunded to your wallet automatically.
            </p>
          )}
        </div>
      )}

      {decision === 'approve' && (
        <p className="text-[11px] text-emerald-300/80">
          Creator gets the green light to post this live and submit the proof URL.
        </p>
      )}

      {error && (
        <div data-testid="decision-error" className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2.5">
          {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={!decision || busy}
        data-testid="decision-submit-btn"
        className="w-full py-3 rounded-lg bg-[#FF4D94] text-white font-bold text-sm disabled:opacity-50 hover:brightness-110 transition flex items-center justify-center gap-2"
        style={{ boxShadow: '0 0 18px rgba(255,77,148,0.3)' }}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send decision'}
      </button>
    </div>
  );
}
